import type { ChatTurn } from '../../hooks/useChatStream';

/**
 * Browser-side persistence for ephemeral chat conversations.
 *
 * Conversations are NOT stored server-side. To survive in-session navigation
 * (e.g. clicking a citation to open content and returning) and page reloads,
 * we cache the running turns in `sessionStorage`, keyed per chat-block instance.
 * `sessionStorage` clears when the tab/browser closes, which keeps the feature
 * ephemeral while fixing the "chat is wiped on return" UX.
 *
 * A size cap evicts the oldest turns so a long conversation can't grow without
 * bound (or blow the storage quota).
 */

const PREFIX = 'sharp.chat.v1';

/** Conservative cap (chars of serialized JSON) per conversation. ~100 KB. */
export const DEFAULT_MAX_BYTES = 100_000;

/**
 * Build a stable storage key for a chat block. Returns undefined when identity
 * is not yet known (no viewId / no block id) so the caller falls back to
 * in-memory-only behavior rather than colliding on a shared key.
 */
export function makeChatStorageKey(parts: {
  viewId?: string;
  componentId?: string;
  fallback?: string;
}): string | undefined {
  const id = parts.componentId || parts.fallback;
  if (!parts.viewId || !id) return undefined;
  return `${PREFIX}:${parts.viewId}:${id}`;
}

function getStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // sessionStorage can throw in private-mode / sandboxed contexts.
    return null;
  }
}

export function loadChatTurns(key: string): ChatTurn[] {
  const store = getStore();
  if (!store) return [];
  try {
    const raw = store.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ChatTurn[]) : [];
  } catch {
    return [];
  }
}

export function saveChatTurns(
  key: string,
  turns: ChatTurn[],
  maxBytes: number = DEFAULT_MAX_BYTES,
): void {
  const store = getStore();
  if (!store) return;
  if (turns.length === 0) {
    try {
      store.removeItem(key);
    } catch {
      /* ignore */
    }
    return;
  }
  // Evict the oldest turns until the payload fits. Drop a user+assistant pair at
  // a time so the retained history stays coherent; drop a single turn if only
  // one remains over budget.
  let trimmed = turns;
  let payload = JSON.stringify(trimmed);
  while (payload.length > maxBytes && trimmed.length > 1) {
    trimmed = trimmed.slice(trimmed.length > 2 ? 2 : 1);
    payload = JSON.stringify(trimmed);
  }
  try {
    store.setItem(key, payload);
  } catch {
    // Quota exceeded or storage disabled — fail silently; chat still works in-memory.
  }
}

export function clearChatTurns(key: string): void {
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
}
